import { useQuery } from "@tanstack/react-query";
import { searchEntities, searchUsers } from "../../api/network";
import { Box, Button, Skeleton, Typography } from "@mui/material";
import UserItem, { UserLoadingItem } from "./UserItem";
import { UserInfo } from "../../types";
import { colors } from "../../theme/palette";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  getSearchTypeEntityValueRoute,
  getSearchTypeUserValueRoute,
} from "../../routers/routeUtils";

const UsersEntitiesSearch = ({ value }: { value: string }) => {
  const { t } = useTranslation();
  const { data: searchDataUsersOnly, isLoading: isSearchDataUsersOnlyLoading } =
    useQuery({
      queryKey: ["searchDataUsersOnly", value],
      queryFn: () => searchUsers(3, 0, value || "", "", "", "", "", ""),
      enabled: !!value,
    });

  const {
    data: searchDataEntitiesOnly,
    isLoading: isSearchDataEntitiesOnlyLoading,
  } = useQuery({
    queryKey: ["searchDataEntitiesOnly", value],
    queryFn: () => searchEntities(3, 0, value, "", "", false, false, ""),
    enabled: !!value,
  });

  const navigate = useNavigate();

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <Box
        sx={{
          paddingBottom: "20px",
          borderBottom: "1px solid #EAECF0",
          marginBottom: "20px",
        }}
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "22px",
          }}
        >
          <Typography
            variant="body13Bold"
            sx={{
              fontWeight: 500,
              color: colors.Black100,
              display: "flex",
              alignItems: "center",
              columnGap: "8px",
            }}
          >
            {isSearchDataUsersOnlyLoading ? (
              <Skeleton
                variant="rounded"
                animation="wave"
                sx={{
                  width: "50px",
                }}
              />
            ) : (
              searchDataUsersOnly?.meta?.totalItems
            )}

            {` ${t("pages.search.peopleRelated")}`}
          </Typography>
        </Box>
        {isSearchDataUsersOnlyLoading && (
          <Box
            sx={{ display: "flex", flexDirection: "column", rowGap: "20px" }}
          >
            {[...Array(3)].map((_, idx) => (
              <UserLoadingItem key={idx} isLast={idx === 2} />
            ))}
          </Box>
        )}

        {searchDataUsersOnly?.items?.length === 0 &&
          !isSearchDataUsersOnlyLoading && (
            <Typography
              variant="body16Regular"
              sx={{
                fontWeight: 500,
                color: colors.Black100,
              }}
            >
              {t("pages.search.noTalents")}
            </Typography>
          )}

        {searchDataUsersOnly?.items?.length > 0 &&
          !isSearchDataUsersOnlyLoading && (
            <Box
              sx={{ display: "flex", flexDirection: "column", rowGap: "20px" }}
            >
              {searchDataUsersOnly?.items.map((user: UserInfo, idx: number) => (
                <UserItem key={user.id} user={user} isLast={idx == 2} />
              ))}
              <Button
                variant="text-sm"
                fullWidth
                onClick={() => navigate(getSearchTypeUserValueRoute(value))}
              >
                {t("pages.search.seeAllTalents")}
              </Button>
            </Box>
          )}
      </Box>
      <Box>
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "22px",
          }}
        >
          <Typography
            variant="body13Bold"
            sx={{
              fontWeight: 500,
              color: colors.Black100,
              display: "flex",
              alignItems: "center",
              columnGap: "8px",
            }}
          >
            {isSearchDataEntitiesOnlyLoading ? (
              <Skeleton
                variant="rounded"
                animation="wave"
                sx={{
                  width: "50px",
                }}
              />
            ) : (
              searchDataEntitiesOnly?.meta?.totalItems
            )}

            {` ${t("pages.search.entitiesRelated")}`}
          </Typography>
        </Box>
        {isSearchDataEntitiesOnlyLoading && (
          <Box
            sx={{ display: "flex", flexDirection: "column", rowGap: "20px" }}
          >
            {[...Array(3)].map((_, idx) => (
              <UserLoadingItem key={idx} isLast={idx === 2} />
            ))}
          </Box>
        )}

        {searchDataEntitiesOnly?.items?.length === 0 &&
          !isSearchDataEntitiesOnlyLoading && (
            <Typography
              variant="body16Regular"
              sx={{
                fontWeight: 500,
                color: colors.Black100,
              }}
            >
              {t("pages.search.noEntities")}
            </Typography>
          )}

        {searchDataEntitiesOnly?.items?.length > 0 &&
          !isSearchDataEntitiesOnlyLoading && (
            <Box
              sx={{ display: "flex", flexDirection: "column", rowGap: "20px" }}
            >
              {searchDataEntitiesOnly?.items.map(
                (user: UserInfo, idx: number) => (
                  <UserItem key={user.id} user={user} isLast={idx == 2} />
                )
              )}
              <Button
                variant="text-sm"
                fullWidth
                onClick={() => navigate(getSearchTypeEntityValueRoute(value))}
              >
                {t("pages.search.seeAllEntities")}
              </Button>
            </Box>
          )}
      </Box>
    </Box>
  );
};

export default UsersEntitiesSearch;
